import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { SortOrder } from '@/components/ui/table'

export type TimeBlockReason =
  | 'Move in'
  | 'Move out'
  | 'Cleaning'
  | 'Building event'
  | 'Maintenance'
  | 'Holiday'
  | 'General'
  | 'Other'

export type TimeBlockSortBy = 'date' | 'endDate'

export interface GetTimeBlocksRequest {
  buildingId?: number
  skip?: number
  limit?: number
  dateStart?: number
  dateEnd?: number
  amenityId?: number[]
  locationId?: number[]
  reason?: TimeBlockReason[]
  sortBy?: TimeBlockSortBy
  sortOrder?: SortOrder
}

export interface TimeBlockNoteRef {
  id: number
  title: string
  noteBody: string
  createdAt: number
  createdBy: number
  timeBlockId: number
}

export interface TimeBlock {
  id: number
  amenityId: { id: number; name: string; buildingId: number }
  locationId: { id: number; name: string } | null
  isPhantomBooking: boolean
  isLinkedBlock: boolean
  linkedReservationId: number | null
  date: number
  endDate: number | null
  startTime: number | null
  endTime: number | null
  createdBy: { firstName: string; lastName: string; role: string } | null
  // null for blocks created before reasons existed - display those as General
  reason: TimeBlockReason | null
  reasonOther: string | null
  description: string | null
  notes: TimeBlockNoteRef[]
  createdAt: number
  updatedAt: number
}

export interface GetTimeBlocksResponse {
  count: number
  blocks: TimeBlock[]
}

export const USE_GET_TIME_BLOCKS_QUERY_KEY = 'useGetTimeBlocks'

export const getTimeBlocksQueryKey = (data: GetTimeBlocksRequest) => [
  USE_GET_TIME_BLOCKS_QUERY_KEY,
  data,
]

export const getTimeBlocks = async (data: GetTimeBlocksRequest) => {
  const response = await authAPI.get<GetTimeBlocksResponse>('/reservation/dashboard/time-block', {
    params: data,
  })
  return response.data
}

// Note: omit dateStart/dateEnd to get every time block of the building ("all dates").
export const useGetTimeBlocks = (data: GetTimeBlocksRequest) =>
  useQuery({
    enabled: !!data.buildingId,
    placeholderData: keepPreviousData,
    queryFn: () => getTimeBlocks(data),
    queryKey: getTimeBlocksQueryKey(data),
  })

export const useInvalidateTimeBlocks = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [USE_GET_TIME_BLOCKS_QUERY_KEY] })
}
